import { isBoolean, isString, isNullOrUndefined } from '@microsoft/1ds-core-js';
import { logError, logNotification, Platform, AudienceGroup } from '@microsoft/oteljs';
// Constants
var DATA_TOKEN = 'Data.';
var CONTRACT_TOKEN = 'zC.';
export var RELEASE_AUDIENCEGROUP = 'Release.AudienceGroup';
export var WAC_RING = 'WAC.Ring';
export var APP_NAME = 'App.Name';
export var APP_PLATFORM = 'App.Platform';
export var APP_VERSION = 'App.Version';
var USER_PRIMARYIDENTITYHASH = 'User.PrimaryIdentityHash';
var USER_PRIMARYIDENTITYSPACE = 'User.PrimaryIdentitySpace';
var USER_TENANTID = 'User.TenantId';
var USER_ISANONYMOUS = 'User.IsAnonymous';
var USER_ISSIGNEDIN = 'User.IsSignedIn';
var SESSION_ID = 'Session.Id';
/**
 * Aria token for Office.Telemetry
 */
export var ARIA_OFFICE_TELEMETRY_TOKEN = 'f998cc5ba4d448d6a1e8e913ff18be94-dd122e0a-fcf8-4dc5-9dbb-6afac5325183-7405'; // Office.Telemetry
export var CUSTOMER_CONTENT_TENANT_TOKEN = 'b22a201c3f1d41d28ccc399ba6cc9ca2-1972c77f-1f79-4283-a0f9-b4ddc4646f55-7121';
export var voidValue = undefined;
/**
 * Add OTelJS DataField fields to a 1DS data object
 * @param data The object to hold 1DS fields
 * @param fields The DataField fields to add
 * @param options The IOneDSFieldOptions for changing how fields are added
 * @return true if event is valid, false is event is invalid
 */
export function addOneDSFields(data, fields, prependDataToken, eventType) {
    var validEvent = true;
    if (fields) {
        var _loop_1 = function (field) {
            var fieldClassification = field.classification;
            var fieldDataType = field.dataType;
            var fieldName = field.name;
            // We can usually only send SystemMetadata or EssentialServiceMetadata
            // CustomerContent is also possible if it's enabled
            // We drop events with other fields
            if (fieldClassification &&
                !(fieldClassification === 4 /* DataClassification.SystemMetadata */ ||
                    fieldClassification === 1 /* DataClassification.EssentialServiceMetadata */ ||
                    (eventType === 2 /* EventType.CustomerContent */ && fieldClassification === 32 /* DataClassification.CustomerContent */) ||
                    (eventType === 3 /* EventType.DirectNumericEvent */ && fieldClassification === 2048 /* DataClassification.DirectMeasurementData */))) {
                validEvent = false;
                return "break";
            }
            // Part C Direct Numeric fields can only be of boolean, double, or int64 data type
            // Prepend data token indicates that the field is a Part C field
            if (eventType === 3 /* EventType.DirectNumericEvent */ &&
                prependDataToken &&
                fieldDataType !== 3 /* DataFieldType.Double */ &&
                fieldDataType !== 1 /* DataFieldType.Boolean */ &&
                fieldDataType !== 2 /* DataFieldType.Int64 */ &&
                fieldName !== 'OTelJS.Version' &&
                fieldName !== 'OTelJS.Sink') {
                logNotification(0 /* LogLevel.Error */, 1 /* Category.Sink */, function () { return 'DNM: Invalid field type ' + fieldName; });
                validEvent = false;
                return "break";
            }
            var modifiedFieldName = void 0;
            if (prependDataToken) {
                if (fieldName.substr(0, 3) === CONTRACT_TOKEN) {
                    modifiedFieldName = CONTRACT_TOKEN + DATA_TOKEN + fieldName.substr(3);
                }
                else {
                    modifiedFieldName = DATA_TOKEN + fieldName;
                }
            }
            else {
                modifiedFieldName = fieldName;
            }
            var eventPropertyType = void 0;
            switch (fieldDataType) {
                case 3 /* DataFieldType.Double */:
                    eventPropertyType = 6 /* EventPropertyType.Double */;
                    break;
                case 2 /* DataFieldType.Int64 */:
                    eventPropertyType = 4 /* EventPropertyType.Int64 */;
                    break;
                case 4 /* DataFieldType.Guid */:
                    eventPropertyType = 8 /* EventPropertyType.Guid */;
                    break;
                case 0 /* DataFieldType.String */:
                    // Strings and booleans are set directly, not as IEventProperties -
                    // tables get created with the appropriate data types on the collector side.
                    // This helps to save about 25% of the event size!
                    // The 1DS SDK assigns a default type of Double to any numbers, so we still
                    // to send the data types for those values.
                    // Limit the maximum allowed string length on a data field to avoid accidentally going over
                    // the maximum allowed serialized size for a 1DS event and having the event dropped.
                    // That and 1ds-post v2.4.4 and v3.0.0 have a bug that will put the code into an infinite
                    // loop if that condition is trigged.
                    // Some callers erroneously pass a non-string value here, which we pass on to the SDK as-is.
                    data[modifiedFieldName] = typeof field.value === 'string' ? field.value.substr(0, 25000) : field.value;
                    return "continue";
                case 1 /* DataFieldType.Boolean */:
                default:
                    data[modifiedFieldName] = field.value;
                    return "continue";
            }
            var eventProperty = {
                value: field.value,
                propertyType: eventPropertyType
            };
            data[modifiedFieldName] = eventProperty;
        };
        for (var _i = 0, fields_1 = fields; _i < fields_1.length; _i++) {
            var field = fields_1[_i];
            var state_1 = _loop_1(field);
            if (state_1 === "break")
                break;
        }
    }
    return validEvent;
}
export function tryCatch(func, errorReturn) {
    try {
        return func();
    }
    catch (error) {
        logError(1 /* Category.Sink */, '1DS Sink', error);
        return errorReturn;
    }
}
export function preprocessEvent(telemetryEvent, preprocessors) {
    var _loop_2 = function (i) {
        var preprocessor = preprocessors[i];
        if (!preprocessor.processEvent(telemetryEvent)) {
            logNotification(2 /* LogLevel.Info */, 1 /* Category.Sink */, function () {
                return "".concat(telemetryEvent.eventName, " suppressed by ").concat(preprocessor.name);
            });
            return { value: false };
        }
    };
    for (var i = 0; i < preprocessors.length; i++) {
        var state_2 = _loop_2(i);
        if (typeof state_2 === "object")
            return state_2.value;
    }
    return true;
}
function logInvalidField(fieldName) {
    logNotification(0 /* LogLevel.Error */, 1 /* Category.Sink */, function () {
        return 'Invalid ' + fieldName;
    });
}
function isNonEmptyString(value) {
    return isString(value) && value !== '';
}
function matchesEnum(value, enumType) {
    // eslint-disable-next-line no-restricted-properties
    return Object.values(enumType).includes(value);
}
function isValidAppPlatform(value) {
    return matchesEnum(value, Platform);
}
function isValidReleaseAudienceGroup(value) {
    return matchesEnum(value, AudienceGroup);
}
function isValidPrimaryIdentitySpace(value) {
    // In v5 we can save some bytes and iterate through the PrimaryIdentitySpace enum directly.
    // Currently that list has a bunch of deprecated values.
    return [
        "MSACID" /* PrimaryIdentitySpace.MSACID */,
        "MSAPUID" /* PrimaryIdentitySpace.MSAPUID */,
        "OrgIdCID" /* PrimaryIdentitySpace.OrgIdCID */,
        "OrgIdPUID" /* PrimaryIdentitySpace.OrgIdPUID */,
        "UserObjectId" /* PrimaryIdentitySpace.UserObjectId */,
        "Other" /* PrimaryIdentitySpace.Other */
    ].includes(value);
}
function isOtherIfDefined(value) {
    return !value || value === "Other" /* PrimaryIdentitySpace.Other */;
}
export function areCoreFieldsValid(data, strict) {
    var isValid = true;
    function validateField(fieldName, validate) {
        if (!validate(data[fieldName])) {
            logInvalidField(fieldName);
            isValid = false;
        }
    }
    function isValidGuid(value) {
        // If not strict, use "i" for case-insensitive match
        var regexpFlags = strict ? '' : 'i';
        // Manually reviewed regex
        // eslint-disable-next-line security/detect-unsafe-regex
        var guid = new RegExp('^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$', regexpFlags);
        return guid.test(value);
    }
    validateField(APP_NAME, isNonEmptyString);
    validateField(APP_PLATFORM, strict ? isValidAppPlatform : isNonEmptyString);
    validateField(APP_VERSION, isNonEmptyString);
    validateField(SESSION_ID, isValidGuid);
    if (strict) {
        validateField(RELEASE_AUDIENCEGROUP, isValidReleaseAudienceGroup);
        validateField(USER_ISSIGNEDIN, isBoolean);
        if (data[USER_ISSIGNEDIN]) {
            validateField(USER_PRIMARYIDENTITYHASH, isNonEmptyString);
            validateField(USER_PRIMARYIDENTITYSPACE, isValidPrimaryIdentitySpace);
            if (["OrgIdCID" /* PrimaryIdentitySpace.OrgIdCID */, "OrgIdPUID" /* PrimaryIdentitySpace.OrgIdPUID */, "UserObjectId" /* PrimaryIdentitySpace.UserObjectId */].includes(data[USER_PRIMARYIDENTITYSPACE])) {
                validateField(USER_TENANTID, isValidGuid);
            }
            else {
                validateField(USER_TENANTID, isNullOrUndefined);
            }
        }
        else {
            validateField(USER_PRIMARYIDENTITYSPACE, isOtherIfDefined);
            validateField(USER_TENANTID, isNullOrUndefined);
        }
    }
    else {
        // If IsAnonymous is false or unset, then User fields must be defined.
        // If IsAnonymous is true, we don't check user fields.
        if (!data[USER_ISANONYMOUS] && data[USER_ISSIGNEDIN] !== false) {
            validateField(USER_PRIMARYIDENTITYHASH, isNonEmptyString);
            validateField(USER_PRIMARYIDENTITYSPACE, isNonEmptyString);
            if (data[USER_TENANTID]) {
                validateField(USER_TENANTID, isValidGuid);
            }
        }
    }
    // IsAnonymous and IsSignedIn cannot have the same value
    if (!isNullOrUndefined(data[USER_ISANONYMOUS]) && data[USER_ISSIGNEDIN] === data[USER_ISANONYMOUS]) {
        logInvalidField(USER_ISANONYMOUS);
        isValid = false;
    }
    return isValid;
}
/**
 * Get a properly formatted timestamp
 * @param event - Telemetry event to calculate timestamp for
 */
export function getTimestamp(event) {
    return event.timestamp ? new Date(event.timestamp) : new Date();
}
/**
 * Get a properly formatted timestamp based on DNM interval
 * @param event - Telemetry event to calculate timestamp for
 */
export function getDnmTimestamp(event) {
    var eventTime = getTimestamp(event);
    // To reduce risk of a privacy incident, direct numeric events
    // will always have seconds and milliseconds zeroed out
    eventTime.setMilliseconds(0);
    eventTime.setSeconds(0);
    eventTime.setMinutes(0);
    if (event.telemetryProperties.dnmInterval === 2 /* DnmInterval.Daily */) {
        eventTime.setHours(0);
    }
    return eventTime;
}
//# sourceMappingURL=InternalUtils.js.map